/**
 * ``useWorkingStateModified`` — derive whether the live **configuration** has
 * diverged from the active Profile (REQ-UI-074).
 *
 * Extracted from the gear menu (2026-06-08, item 7f) so it can drive BOTH the
 * gear menu's section logic AND the top bar's "Profile: «name» (modified)"
 * line — the active-Profile status line moved out of the menu into the top bar,
 * and both surfaces need the same "(modified)" derivation.
 *
 * # Full live snapshot (architect 2026-06-08, correctness fix)
 *
 * The divergence is computed over the **full live document** — ``workingState``
 * with the **live dockview layout** and the **live map camera** folded in
 * (:func:`useLiveSnapshotDocument`) — NOT ``workingState`` alone. So a pan/zoom
 * or a panel rearrange flips the marker to "(modified)", exactly as
 * Update-current's full-document diff now does (they hash the same snapshot, so
 * the indicator and the update outcome can never disagree). Reactivity comes
 * from :func:`useLiveSnapshotDocument` (the working state, the map-viewport
 * Zustand selector, and the multi-pane ``layoutRevision`` nonce); the hash is
 * recomputed whenever that snapshot or the active-Profile pointer changes.
 *
 * Returns ``false`` when there is no active Profile (the consumer shows
 * "Custom (unsaved)" instead) or while the async hash is in flight; flips to
 * ``true`` once the canonical hash of the full live document differs from the
 * Profile's ``applied_document_sha256``.
 *
 * Cross-references:
 *   - REQ-UI-074 (active-Profile + "(modified)" marker).
 *   - ADR-033 §3.2 / §5 (working-state model; save folds layout + camera).
 *   - ADR-024 §6 (layout-change signal — ``MultiPaneApi.layoutRevision``).
 */
import { useEffect, useState } from "react";
import type { ActiveProfileRef, ProfileDocument } from "@/auth/session-types";
import { sha256CanonicalJson } from "@/profiles/profile-hash";
import { useLiveSnapshotDocument } from "@/profiles/use-save-profile";

/**
 * Whether the live configuration has diverged from ``activeProfile`` (REQ-UI-074).
 *
 * @param workingState - The live working-state document (scalar slices). The
 *     hook folds the live layout + map camera in itself, so callers pass only
 *     the working state and get a full-snapshot comparison.
 * @param activeProfile - The applied Profile pointer, or ``null`` when the
 *     working state is unnamed / custom.
 * @returns ``true`` once the full live document's canonical hash differs from
 *     the active Profile's ``applied_document_sha256``; ``false`` when there is
 *     no active Profile or while the async hash is computing.
 */
export function useWorkingStateModified(
  workingState: ProfileDocument,
  activeProfile: ActiveProfileRef | null,
): boolean {
  // The full live document — working state + live layout + live camera — so the
  // marker reflects layout / viewport changes the scalar working state never
  // sees (consistent with Update-current's full-document diff).
  const liveDocument = useLiveSnapshotDocument(workingState);
  const [modified, setModified] = useState(false);
  useEffect(() => {
    if (activeProfile === null) {
      setModified(false);
      return;
    }
    let cancelled = false;
    void sha256CanonicalJson(liveDocument).then((hash) => {
      if (!cancelled) {
        setModified(hash !== activeProfile.applied_document_sha256);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [liveDocument, activeProfile]);
  return modified;
}
